import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Modal, TextInput, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../../components/Button';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import type { ProductView } from '../../../../../services/products/products.service';
import classes from './product-picker-modal.module.scss';

type ProductPickerModalProps = {
  opened: boolean;
  title: string;
  hint?: string;
  products: ProductView[];
  itemsByProductId?: Record<number, { id: number; name: string; groupName: string }[]>;
  excludeProductId?: number | null;
  isSubmitting: boolean;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: (productIds: number[], itemIds: number[]) => void;
};

// Architectural summary: Multi-select picker of products and, when expanded, their combo items.
const ProductPickerModalComponent: React.FC<ProductPickerModalProps> = ({
  opened,
  title,
  hint,
  products,
  itemsByProductId = {},
  excludeProductId = null,
  isSubmitting,
  confirmLabel,
  onClose,
  onConfirm,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (opened) {
      setSearch('');
      setSelectedIds(new Set());
      setSelectedItemIds(new Set());
      setExpandedIds(new Set());
    }
  }, [opened]);

  const candidateProducts = useMemo(
    () => products.filter((product) => product.isActive && product.id !== excludeProductId),
    [products, excludeProductId],
  );

  const normalizedSearch = search.trim().toLowerCase();
  const filteredProducts = normalizedSearch
    ? candidateProducts.filter((product) => product.name.toLowerCase().includes(normalizedSearch))
    : candidateProducts;

  const toggleIn = (setter: React.Dispatch<React.SetStateAction<Set<number>>>) => (id: number) => {
    setter((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const toggle = toggleIn(setSelectedIds);
  const toggleItem = toggleIn(setSelectedItemIds);
  const toggleExpanded = toggleIn(setExpandedIds);
  const selectedCount = selectedIds.size + selectedItemIds.size;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
      closeButtonProps={{ disabled: isSubmitting, 'aria-label': 'סגור', 'data-testid': 'product-picker-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <div className={classes.body}>
        {hint ? <div className={classes.hint}>{hint}</div> : null}
        <TextInput
          data-testid="product-picker-search"
          placeholder="חיפוש לפי שם"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          onContextMenu={(event) => event.preventDefault()}
          onSelect={(event) => {
            const input = event.currentTarget;
            const end = input.value.length;
            if (input.selectionStart !== end || input.selectionEnd !== end) {
              input.setSelectionRange(end, end);
            }
          }}
          radius="md"
          aria-label="חיפוש מוצרים"
        />
        <div className={classes.list}>
          {filteredProducts.length === 0 ? (
            <div className={classes.empty}>לא נמצאו מוצרים.</div>
          ) : (
            filteredProducts.map((product) => {
              const items = itemsByProductId[product.id] ?? [];
              const expanded = expandedIds.has(product.id);
              return (
                <React.Fragment key={product.id}>
                  <div className={classes.row} data-testid={`product-picker-row-${product.id}`}>
                    {items.length > 0 ? (
                      <UnstyledButton
                        className={classes.expand}
                        aria-label={`הצג פריטים של ${product.name}`}
                        aria-expanded={expanded}
                        data-testid={`product-picker-expand-${product.id}`}
                        onClick={() => toggleExpanded(product.id)}
                      >
                        <FontAwesomeIcon icon={(expanded ? faChevronDown : faChevronLeft) as IconProp} size="xs" />
                      </UnstyledButton>
                    ) : (
                      <span className={classes.expandSpacer} />
                    )}
                    <label className={classes.rowLabel}>
                      <Checkbox
                        checked={selectedIds.has(product.id)}
                        onChange={() => toggle(product.id)}
                        aria-label={product.name}
                      />
                      <span className={classes.rowName}>{product.name}</span>
                    </label>
                  </div>
                  {expanded && items.map((item) => (
                    <div
                      key={item.id}
                      className={`${classes.row} ${classes.itemRow}`}
                      data-testid={`product-picker-item-row-${item.id}`}
                    >
                      <label className={classes.rowLabel}>
                        <Checkbox
                          checked={selectedItemIds.has(item.id)}
                          onChange={() => toggleItem(item.id)}
                          aria-label={item.name}
                        />
                        <span className={classes.rowName}>{item.name}</span>
                      </label>
                      <span className={classes.itemGroup}>{item.groupName}</span>
                    </div>
                  ))}
                </React.Fragment>
              );
            })
          )}
        </div>
        <div className={classes.footer}>
          <span className={classes.count}>{selectedCount > 0 ? `${selectedCount} נבחרו` : ''}</span>
          <Button variant="cancel" onClick={onClose} disabled={isSubmitting} dataTestId="product-picker-cancel">
            ביטול
          </Button>
          <Button
            variant="save"
            disabled={isSubmitting || selectedCount === 0}
            onClick={() => onConfirm(Array.from(selectedIds), Array.from(selectedItemIds))}
            dataTestId="product-picker-confirm"
          >
            {isSubmitting ? 'מעתיק…' : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const ProductPickerModal = React.memo(ProductPickerModalComponent);
